// src/components/layout/AppSidebar.tsx
// (Updated version with Distribuidores added below Propostas)

import { useState, useEffect } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { usePermissions } from "@/hooks/usePermissions"
import { useAuthorization } from "@/contexts/AuthorizationContext"
import { supabase } from "@/integrations/supabase/client"
import { Badge } from "@/components/ui/badge"
import {
  LayoutDashboard,
  Users,
  FileText,
  BarChart3,
  Settings,
  Zap,
  Calendar,
  Target,
  Sun,
  Handshake,
  MapPin,
  DollarSign,
  Clock,
  Calculator,
  Ruler,
  Truck, // New import for Distribuidores icon
  Zap as ZapIcon, // New import for Inversores
  UserCog, // Import for Vendedores icon
  ClipboardCheck, // Import for Revisão icon
  CheckSquare, // Import for Tarefas icon
  ClipboardList, // Import for Cadastros icon
  FileSignature, // Import for Assinaturas Pendentes
  Wrench,
  Layers,
  Receipt,
  Plug,
  FileSpreadsheet,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

const menuItems = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
    description: "Visão geral e métricas",
    permission: "dashboard:view"
  },
  {
    title: "Calculadoras",
    url: "/calculadora",
    icon: Calculator,
    description: "Simulador solar e calculadora financeira",
    permission: "dimensioning:read"
  },
  {
    title: "Carregadores Veiculares",
    url: "/carregadores",
    icon: ZapIcon,
    description: "Precificação e propostas de carregadores",
    permission: "dimensioning:read"
  },
  {
    title: "Orçamentos de Obras",
    url: "/orcamentos-carregadores",
    icon: FileSpreadsheet,
    description: "Orçamentação de instalação de carregadores"
  },
  {
    title: "Adequações e Reformas",
    url: "/adequacoes-reformas",
    icon: Wrench,
    description: "Propostas de adequação FV/elétrica e reformas",
    permission: "dimensioning:read"
  },
  {
    title: "CRM Clientes",
    url: "/pipeline",
    icon: Sun,
    description: "Gestão de funil de vendas",
    permission: "leads:read"
  },
  {
    title: "CRM Parceiros",
    url: "/pipeline-parceiros",
    icon: Handshake,
    description: "Prospecção de parcerias Sun7",
    permission: "leads:read"
  },
  {
    title: "Clientes",
    url: "/clientes",
    icon: Users,
    description: "Gestão de leads e clientes",
    permission: "clients:read"
  },
  {
    title: "Dimensionamento",
    url: "/dimensionamento",
    icon: Ruler,
    description: "Dimensionamento de sistemas solares",
    permission: "dimensioning:read"
  },
  {
    title: "Propostas FV",
    url: "/propostas",
    icon: FileText,
    description: "Cotações e propostas",
    permission: "proposals:read"
  },
  {
    title: "Contratos FV",
    url: "/contratos",
    icon: FileText,
    description: "Geração de contratos",
    permission: "contracts:read"
  },
  {
    title: "Tarefas",
    url: "/tarefas",
    icon: CheckSquare,
    description: "Gerenciamento de tarefas de vendas",
    permission: "leads:read"
  },
  { // New item added here, below Propostas
    title: "Distribuidores",
    url: "/distribuidores",
    icon: Truck,
    description: "Gestão de distribuidores",
    permission: "distributors:read"
  },
  {
    title: "Parceiros",
    url: "/parceiros",
    icon: Users,
    description: "Gerenciamento de parceiros",
    permission: "parceiros:read"
  },
  {
    title: "Vendedores",
    url: "/vendedores",
    icon: UserCog,
    description: "Gerenciamento de vendedores",
    permission: "vendedores:read"
  },
  {
    title: "Instalações",
    url: "/instalacoes",
    icon: Zap,
    description: "Acompanhamento de obras",
    permission: "contracts:read"
  },
  {
    title: "Relatórios",
    url: "/relatorios",
    icon: BarChart3,
    description: "Análises e insights",
    permission: "reports:read"
  },
]

const secondaryItems = [
  {
    title: "Calendário",
    url: "/calendario",
    icon: Calendar,
    description: "Calendário e compromissos"
  },
  {
    title: "Metas",
    url: "/metas",
    icon: Target,
    description: "Objetivos e KPIs"
  },
  {
    title: "Territórios",
    url: "/territorios",
    icon: MapPin,
    description: "Gestão regional"
  },
  {
    title: "Financeiro",
    url: "/financeiro",
    icon: DollarSign,
    description: "Controle financeiro"
  },
]

const vendedorItems = [
  {
    title: "Dimensionamentos Pendentes",
    url: "/revisao-precificacao",
    icon: Clock,
    description: "Ver seus dimensionamentos aguardando revisão",
    showBadge: false
  }
]

const adminOnlyItems = [
  {
    title: "Revisão de Precificação",
    url: "/revisao-precificacao",
    icon: ClipboardCheck,
    description: "Aprovar propostas de vendedores",
    showBadge: true
  },
  {
    title: "Cadastros",
    url: "/cadastros",
    icon: ClipboardList,
    description: "Cadastros pendentes de parceiros",
    showBadge: true
  }
]

const adminOrFinanceiroItems = [
  {
    title: "Assinaturas Pendentes",
    url: "/assinaturas-pendentes-sun7",
    icon: FileSignature,
    description: "Assinar contratos como Sun7 (Assinafy)",
    showBadge: false
  },
  {
    title: "Notas Fiscais",
    url: "/notas-fiscais",
    icon: Receipt,
    description: "Emissão de NF-e (Focus NFe)",
    showBadge: false
  }
]

const systemItems = [
  {
    title: "Configurações",
    url: "/configuracoes",
    icon: Settings,
    description: "Configurações do sistema"
  },
  {
    title: "Módulos Fotovoltaicos",
    url: "/modulos-fotovoltaicos",
    icon: Sun,
    description: "Gestão de módulos fotovoltaicos"
  },
  {
    title: "Inversores e Micro",
    url: "/inversores",
    icon: ZapIcon,
    description: "Gestão de inversores"
  },
  {
    title: "Baterias",
    url: "/baterias",
    icon: Layers,
    description: "Gestão de baterias"
  },
  {
    title: "Catálogo de Carregadores",
    url: "/modelos-carregadores",
    icon: Plug,
    description: "Especificações técnicas dos carregadores"
  }
]

export function AppSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar()
  const location = useLocation()

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false)
  }
  const currentPath = location.pathname
  const permissions = usePermissions()
  
  // Usar useAuthorization de forma defensiva
  let authz;
  try {
    authz = useAuthorization();
  } catch (e) {
    console.error("❌ AppSidebar: Erro ao usar useAuthorization:", e);
    authz = null;
  }
  
  const userRole = authz?.userRole;
  
  // Estado para contagem de propostas aguardando revisão
  const [propostasCount, setPropostasCount] = useState<number>(0)
  // Estado para contagem de cadastros pendentes (parceiros)
  const [cadastrosCount, setCadastrosCount] = useState<number>(0)

  const isActive = (path: string) => currentPath === path || currentPath.startsWith(path + "/")

  // Função para verificar se o usuário pode acessar um item do menu
  const canAccessMenuItem = (item: { permission?: string }) => {
    if (!item.permission) return true;
    
    // Verificar se as permissões estão carregadas
    if (!permissions || !permissions.leads || !permissions.clients || !permissions.contracts || !permissions.proposals || !permissions.reports || !permissions.dimensioning) {
      return false; // Não mostrar nenhum item enquanto carrega
    }
    
    switch (item.permission) {
      case 'dashboard:view':
        return permissions.canViewDashboard?.() || false;
      case 'leads:read':
        return permissions.leads?.canRead?.() || false;
      case 'clients:read':
        return permissions.clients?.canRead?.() || false;
      case 'dimensioning:read':
        return permissions.dimensioning?.canRead?.() || false;
      case 'proposals:read':
        return permissions.proposals?.canRead?.() || false;
      case 'contracts:read':
        return permissions.contracts?.canRead?.() || false;
      case 'distributors:read':
        return permissions.distributors?.canRead?.() || false;
      case 'parceiros:read':
        return permissions.parceiros?.canRead?.() || false;
      case 'vendedores:read':
        return permissions.vendedores?.canRead?.() || false;
      case 'reports:read':
        return permissions.reports?.canRead?.() || false;
      case 'admin:only':
        return permissions.isAdmin?.() || false;
      default:
        return true;
    }
  };

  // Filtrar itens do menu baseado nas permissões
  const filteredMenuItems = menuItems.filter(item => {
    // Ocultar Instalações para vendedores e parceiros
    if ((userRole === 'vendedor' || userRole === 'parceiro') && item.url === '/instalacoes') {
      return false;
    }
    // Página global de Tarefas (Kanban): apenas admin e financeiro
    if (item.url === '/tarefas' && userRole !== 'admin' && userRole !== 'financeiro') {
      return false;
    }
    if (item.url === '/orcamentos-carregadores' && userRole !== 'admin' && userRole !== 'financeiro') {
      return false;
    }
    return canAccessMenuItem(item);
  });
  // Filtrar secondaryItems: ocultar Metas, Territórios e Financeiro para parceiros e vendedores
  const filteredSecondaryItems = secondaryItems.filter(item => {
    // Se for parceiro ou vendedor, ocultar Metas, Territórios e Financeiro
    if (userRole === 'parceiro' || userRole === 'vendedor') {
      const restrictedPages = ['/metas', '/territorios', '/financeiro'];
      return !restrictedPages.includes(item.url);
    }
    return canAccessMenuItem(item);
  });
  const filteredVendedorItems = userRole === 'vendedor' ? vendedorItems : [];
  const filteredAdminOnlyItems = userRole === 'admin' ? adminOnlyItems : [];
  const filteredAdminOrFinanceiroItems = (userRole === 'admin' || userRole === 'financeiro') ? adminOrFinanceiroItems : [];
  const filteredSystemItems = systemItems.filter(canAccessMenuItem);

  // Buscar contagem de propostas aguardando revisão (apenas para admin)
  useEffect(() => {
    if (userRole === 'admin') {
      const fetchCount = async () => {
        try {
          const { data, error } = await supabase
            .rpc('get_propostas_aguardando_revisao_count');

          if (error) throw error;

          if (data && data.length > 0) {
            setPropostasCount(data[0].count_total || 0);
          } else {
            setPropostasCount(0);
          }
        } catch (error) {
          console.error('Erro ao buscar contagem de propostas:', error);
        }
      };

      // Buscar imediatamente
      fetchCount();

      // Atualizar a cada 30 segundos
      const interval = setInterval(fetchCount, 30000);

      return () => clearInterval(interval);
    }
  }, [userRole]);

  // Buscar contagem de cadastros pendentes (apenas para admin)
  useEffect(() => {
    if (userRole === 'admin') {
      const fetchCadastrosCount = async () => {
        try {
          const { data, error } = await supabase
            .rpc('get_cadastros_pendentes_count');

          if (error) throw error;

          if (data && data.length > 0) {
            setCadastrosCount(data[0].count_total || 0);
          } else {
            setCadastrosCount(0);
          }
        } catch (error) {
          console.error('Erro ao buscar contagem de cadastros pendentes:', error);
        }
      };

      fetchCadastrosCount();
      const interval = setInterval(fetchCadastrosCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  return (
    <Sidebar className="border-r border-border bg-card">
      <SidebarHeader className="border-b border-border px-4 py-6">
        <div className="flex items-center gap-3">
          <div className="flex h-[53px] w-[53px] items-center justify-center rounded-lg overflow-hidden">
            <img 
              src="/Logo 2 (Amarelo&Azul)_transp.png" 
              alt="SUN7 Energy Logo" 
              className="h-full w-full object-contain"
            />
          </div>
          {state === "expanded" && (
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-foreground">SUN7 LINK</h2>
              <p className="text-xs text-muted-foreground">Plataforma Solar</p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          <SidebarGroupLabel>Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={isActive(item.url)}
                    tooltip={state === "collapsed" ? item.description : undefined}
                  >
                    <NavLink 
                      to={item.url} 
                      onClick={closeMobileSidebar}
                      className={({ isActive }) =>
                        isActive 
                          ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                          : "hover:bg-accent/50"
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {state === "expanded" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Ferramentas</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredSecondaryItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={isActive(item.url)}
                    tooltip={state === "collapsed" ? item.description : undefined}
                  >
                    <NavLink 
                      to={item.url}
                      onClick={closeMobileSidebar}
                      className={({ isActive }) =>
                        isActive 
                          ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                          : "hover:bg-accent/50"
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {state === "expanded" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Seção especial para vendedores */}
        {filteredVendedorItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Meus Dimensionamentos</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredVendedorItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive(item.url)}
                      tooltip={state === "collapsed" ? item.description : undefined}
                    >
                      <NavLink 
                        to={item.url}
                        onClick={closeMobileSidebar}
                        className={({ isActive }) =>
                          isActive 
                            ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                            : "hover:bg-accent/50"
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {state === "expanded" && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Seção Sistema - Para todos os usuários */}
        {(filteredSystemItems.length > 0 || filteredAdminOnlyItems.length > 0 || filteredAdminOrFinanceiroItems.length > 0) && (
          <SidebarGroup>
            <SidebarGroupLabel>Sistema</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {/* Itens exclusivos do admin (Revisão de Precificação, Cadastros) */}
                {filteredAdminOnlyItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive(item.url)}
                      tooltip={state === "collapsed" ? item.description : undefined}
                    >
                      <NavLink 
                        to={item.url}
                        onClick={closeMobileSidebar}
                        className={({ isActive }) =>
                          isActive 
                            ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                            : "hover:bg-accent/50"
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {state === "expanded" && (
                          <span className="flex-1">{item.title}</span>
                        )}
                        {/* Badge de contagem (revisão de precificação ou cadastros pendentes) */}
                        {item.showBadge && state === "expanded" && (() => {
                          const count = item.url === '/revisao-precificacao' ? propostasCount : item.url === '/cadastros' ? cadastrosCount : 0;
                          return count > 0 ? (
                            <Badge
                              variant="destructive"
                              className="ml-auto"
                            >
                              {count}
                            </Badge>
                          ) : null;
                        })()}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {/* Assinaturas Pendentes - admin e financeiro */}
                {filteredAdminOrFinanceiroItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive(item.url)}
                      tooltip={state === "collapsed" ? item.description : undefined}
                    >
                      <NavLink 
                        to={item.url}
                        onClick={closeMobileSidebar}
                        className={({ isActive }) =>
                          isActive 
                            ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                            : "hover:bg-accent/50"
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {state === "expanded" && <span className="flex-1">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {/* Itens do sistema para todos os usuários */}
                {filteredSystemItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive(item.url)}
                      tooltip={state === "collapsed" ? item.description : undefined}
                    >
                      <NavLink 
                        to={item.url}
                        onClick={closeMobileSidebar}
                        className={({ isActive }) =>
                          isActive 
                            ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" 
                            : "hover:bg-accent/50"
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {state === "expanded" && (
                          <span className="flex-1">{item.title}</span>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      {/* Versão do build em produção: confere se o deploy está atualizado (commit hash) */}
      {(import.meta.env.VITE_APP_VERSION && import.meta.env.VITE_APP_VERSION !== "dev") && (
        <SidebarFooter className="border-t border-sidebar-border">
          <div className="px-2 py-1.5 text-[10px] text-muted-foreground font-mono" title="Commit do deploy (para conferir se produção está atualizada)">
            build {import.meta.env.VITE_APP_VERSION}
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  )
}
